import type { Board } from "../board";
import { directionDelta, directionFromVector } from "../direction";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { BasicAI } from "../ai/basicAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { HomingMissile } from "../things/homingMissile";
import { XY } from "../xy";

export class RangedMobTest extends Mob {
	static key = "RangedMobTest";
	private ai: BasicAI | null = null;

	constructor(xy: XY) {
		super(xy);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLOOD;
	}

	processMobAction(_board: Board): void {
		if (!this.ai) {
			this.ai = new BasicAI(this);
		}
		this.ai.processTurn();
	}

	canAttack(target: PhysicalThingWithHp): boolean {
		if (!target) return false;
		const targetXY = target.xy;
		const delta = targetXY.subtract(this.xy);

		const isAligned = delta.x === 0 || delta.y === 0 || Math.abs(delta.x) === Math.abs(delta.y);
		if (!isAligned) return false;

		return this.isInLineOfSight(this.xy, targetXY);
	}

	private isInLineOfSight(origin: XY, target: XY): boolean {
		const between = this.getCellsBetween(origin, target);
		return between.every((xy) => this.board!.isPassable(xy));
	}

	private getCellsBetween(from: XY, to: XY): XY[] {
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		const steps = Math.max(Math.abs(dx), Math.abs(dy));
		if (steps <= 1) return [];

		const stepX = Math.sign(dx);
		const stepY = Math.sign(dy);
		const result: XY[] = [];
		for (let i = 1; i < steps; i++) {
			result.push(new XY(from.x + stepX * i, from.y + stepY * i));
		}
		return result;
	}

	attack(target: PhysicalThingWithHp): void {
		const targetXY = target.xy;
		if (targetXY.isInSquareRadius(this.xy, 1)) {
			super.attack(target);
			return;
		}

		const dx = targetXY.x - this.xy.x;
		const dy = targetXY.y - this.xy.y;
		const direction = directionFromVector(new XY(Math.sign(dx), Math.sign(dy)));
		if (!direction) return;

		const missileXY = this.xy.add(directionDelta(direction));
		if (!this.board!.isInBounds(missileXY)) return;

		const missile = new HomingMissile(missileXY, direction, targetXY);
		this.board!.addThing(missile);
	}

	generateCorpse() {
		return null;
	}

	static deserialize(data: any): RangedMobTest {
		const m = new RangedMobTest(deserializeXY(data.xy));
		m.restoreMobFields(data);
		return m;
	}
}
